'use client'

import { Button, ButtonVariants } from '../../atoms/Button'
import {
    ICON_BUTTON_ARIA_LABEL_MESSAGE_KEYS,
    resolveIconButtonAriaLabel,
} from '../../../constants/iconButtonAriaLabelMessageKeys'
import { MESSAGE_KEYS } from '../../../constants/messageKeys'
import { ComponentTranslationContext, DictionaryContext } from '../../../providers'
import { DeleteIcon, EditIcon } from '../../icons'
import React, { ReactElement, ReactNode, useContext, useRef } from 'react'
import { Spinner } from '../../atoms'

interface FileInputProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
    onChange: (value: File | null) => void
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    value: any
    name?: string
    disabled?: boolean
    /** While the selected file is being processed (i.e. by the backend) we use this prop to show a loading spinner and prevent changes to the file input */
    isProcessing?: boolean
    /** Comma separated list of extensions that are accepted by the file input.
     * E.g.: ".jpg, .jpeg, .png,"
     */
    acceptedExtensions?: string
    additionalButtons?: ReactNode
}

export const FileInput = ({
    onChange,
    value,
    name,
    additionalButtons,
    disabled = false,
    isProcessing = false,
    acceptedExtensions = '.jpg, .jpeg, .png, .webp',
    ...props
}: FileInputProps): ReactElement => {
    const inputRef = useRef<HTMLInputElement>(null)
    const { getLabel } = useContext(ComponentTranslationContext)
    const dictionary = useContext(DictionaryContext)

    const handleFileSelect = () => {
        if (disabled) return
        inputRef.current?.click()
    }

    const clearFile = () => {
        if (disabled) return
        onChange(null)
    }

    const areActionsAvailable = !disabled && !isProcessing

    return (
        <div className={`input-wrapper flex items-center border w-full ${props.className ?? ''}`} data-cy="input-file">
            {!value && (
                <Button
                    onClick={() => handleFileSelect()}
                    data-testid="add-button"
                    className="w-full"
                    key="add-button"
                    disabled={disabled || isProcessing}
                >
                    {getLabel(MESSAGE_KEYS.ACTION_ADD)}
                </Button>
            )}
            {value && (
                <div className="flex w-full items-center">
                    <span className="truncate px-3 py-2 density-lg:py-3" title={value?.name}>
                        {value?.name}
                    </span>
                    {isProcessing && <Spinner className="shrink-0 ml-2" />}
                    {areActionsAvailable && (
                        <>
                            <Button
                                icon={<EditIcon />}
                                variant={ButtonVariants.QUIET}
                                onClick={() => handleFileSelect()}
                                data-testid="edit-button"
                                className="shrink-0"
                                key="edit-button"
                                aria-label={resolveIconButtonAriaLabel(
                                    dictionary,
                                    ICON_BUTTON_ARIA_LABEL_MESSAGE_KEYS.EDIT_FILE_SELECTION,
                                )}
                            />
                            <Button
                                icon={<DeleteIcon />}
                                variant={ButtonVariants.DANGER_QUIET}
                                onClick={clearFile}
                                data-testid="delete-button"
                                className="shrink-0"
                                key="delete-button"
                                aria-label={resolveIconButtonAriaLabel(
                                    dictionary,
                                    ICON_BUTTON_ARIA_LABEL_MESSAGE_KEYS.REMOVE_FILE_SELECTION,
                                )}
                            />
                            {additionalButtons}
                        </>
                    )}
                </div>
            )}
            <input
                className="hidden"
                type="file"
                onChange={(e) => onChange(e.target.files?.[0] ?? null)}
                ref={inputRef}
                name={name}
                accept={acceptedExtensions}
            />
        </div>
    )
}
